import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Bot,
  Workflow,
  History,
  Wallet,
  Inbox,
  Coins,
  BookOpen,
  LogOut,
  ArrowLeft,
} from 'lucide-react';
import { cn, formatUSD } from '../lib/utils';
import { Logo } from './Logo';
import { approvalsService } from '../services/approvalsService';
import { budgetService } from '../services/budgetService';
import { usageService, type Wallet as WalletData } from '../services/usageService';
import { useAuth } from '../auth/AuthContext';
import { agentStudioPath } from '../lib/routes';
import { AGENT_STUDIO_PERMISSIONS as P, useAgentStudioPermissions } from '../lib/permissions';

const nav = [
  { to: agentStudioPath(), label: 'Dashboard', icon: LayoutDashboard, permission: P.dashboardView, end: true },
  { to: agentStudioPath('activities'), label: 'Minhas Atividades', icon: Inbox, permission: P.activitiesView, badgeKey: 'approvals' },
  { to: agentStudioPath('agents'), label: 'Agentes', icon: Bot, permission: P.agentsView },
  { to: agentStudioPath('flows'), label: 'Fluxos', icon: Workflow, permission: P.flowsView },
  { to: agentStudioPath('knowledge-bases'), label: 'Bases de Conhecimento', icon: BookOpen, permission: P.knowledgeBasesView },
  { to: agentStudioPath('runs'), label: 'Execuções', icon: History, permission: P.runsView },
  { to: agentStudioPath('budget'), label: 'Áreas & Budget', icon: Wallet, permission: P.budgetView },
  { to: agentStudioPath('costs'), label: 'Custos', icon: Coins, permission: P.costsView },
] as const;

export function Sidebar() {
  const { user, logout } = useAuth();
  const { has } = useAgentStudioPermissions();
  const location = useLocation();
  const [pending, setPending] = useState(0);
  const [wallet, setWallet] = useState<WalletData | null>(null);
  const canViewActivities = has(P.activitiesView);

  useEffect(() => {
    if (canViewActivities) {
      Promise.all([
        approvalsService.pendingCountForUser().catch(() => 0),
        budgetService.listPendingGrants().then((g) => g.length).catch(() => 0),
      ]).then(([a, g]) => setPending(a + g));
    } else {
      setPending(0);
    }
    usageService.wallet().then(setWallet).catch(() => setWallet(null));
  }, [location.pathname, canViewActivities]);

  const displayName = user?.name ?? 'Usuario';
  const initials = displayName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');

  return (
    <aside className="flex w-[265px] shrink-0 flex-col bg-navy text-white">
      <div className="px-5 py-5">
        <div className="rounded-xl bg-white px-3 py-2.5 shadow-sm">
          <Logo variant="full" theme="light" />
        </div>
        <p className="mt-2 pl-1 text-[11px] font-medium uppercase tracking-wide text-white/55">
          Agent Studio
        </p>
      </div>

      <nav className="flex-1 space-y-1 px-3 py-2">
        <div className="mb-3 border-b border-white/10 pb-3">
          <Link
            to="/home"
            className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-white/75 transition-colors hover:bg-white/5 hover:text-white"
          >
            <ArrowLeft className="h-[18px] w-[18px]" />
            <span className="flex-1">Sistema Interno</span>
          </Link>
        </div>

        {nav.filter((item) => has(item.permission)).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={'end' in item ? item.end : false}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-brand-orange text-white'
                  : 'text-white/70 hover:bg-white/5 hover:text-white',
              )
            }
          >
            <item.icon className="h-[18px] w-[18px]" />
            <span className="flex-1">{item.label}</span>
            {'badgeKey' in item && item.badgeKey === 'approvals' && pending > 0 && (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-orange px-1.5 text-[11px] font-semibold text-white">
                {pending}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      {wallet && (
        <div className="border-t border-white/10 px-5 py-3">
          <div className="flex items-center justify-between text-[11px] text-white/60">
            <span className="flex items-center gap-1.5"><Coins className="h-3.5 w-3.5" /> Seu saldo</span>
            <span className={cn('font-semibold', wallet.available <= 0 ? 'text-brand-orange' : 'text-white')}>{formatUSD(wallet.available)}</span>
          </div>
          <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-white/10">
            <div
              className={cn('h-full rounded-full', wallet.available <= 0 ? 'bg-brand-orange' : 'bg-success')}
              style={{ width: `${wallet.monthlyBudget > 0 ? Math.min(100, Math.round((wallet.monthlyRemaining / wallet.monthlyBudget) * 100)) : (wallet.available > 0 ? 100 : 0)}%` }}
            />
          </div>
          <div className="mt-1 text-[10px] text-white/40">
            gasto: {formatUSD(wallet.monthlySpent)} - resta {formatUSD(wallet.monthlyRemaining)}/{formatUSD(wallet.monthlyBudget)}{wallet.topup > 0 ? ` - extra: ${formatUSD(wallet.topup)}` : ''}
          </div>
        </div>
      )}

      <div className="border-t border-white/10 px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-xs font-semibold">
            {initials || 'U'}
          </div>
          <div className="min-w-0 flex-1 leading-tight">
            <div className="truncate text-xs font-medium">{displayName}</div>
            <div className="truncate text-[11px] text-white/50">{user?.email ?? ''}</div>
          </div>
          <button
            type="button"
            onClick={() => logout()}
            title="Sair"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white/60 transition-colors hover:bg-white/10 hover:text-white"
          >
            <LogOut className="h-[18px] w-[18px]" />
          </button>
        </div>
      </div>
    </aside>
  );
}
